import { ShoppingCart, Plus, Minus, ChevronDown, ChevronUp, Loader, X } from "lucide-react";
import { motion } from "framer-motion";
import { Link, useParams } from "react-router-dom";
import { useState, useEffect } from "react";
import { useCart } from "../../../Api/CartContext"; // Import the cart context
import { BASE_URL } from "../../../Api/Config";

const RestaurantMenu = () => {
    const { id } = useParams(); // Get restaurant ID from URL params
    const [displayWhole, setDisplayWhole] = useState({});
    const [menuData, setMenuData] = useState([]);
    const [restaurant, setRestaurant] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [showCategories, setShowCategories] = useState(false);

    // Use cart context
    const {
        cart,
        addToCart,
        updateQuantity,
        removeFromCart,
        getItemQuantity,
        loading: cartLoading,
        error: cartError,
        clearError
    } = useCart();

    // Fetch menu data from API
    useEffect(() => {
        const fetchMenuData = async () => {
            try {
                setLoading(true);
                const response = await fetch(`${BASE_URL}/public/restaurant/${id}`);

                if (!response.ok) {
                    throw new Error(`Failed to fetch menu: ${response.statusText}`);
                }

                const result = await response.json();

                if (result.success) {
                    setMenuData(result.data);
                    setRestaurant(result.restaurant);

                    // Initialize all sections as expanded by default
                    const initialDisplayState = {};
                    result.data.forEach((_, idx) => {
                        initialDisplayState[idx] = true;
                    });
                    setDisplayWhole(initialDisplayState);
                } else {
                    throw new Error('API returned unsuccessful response');
                }
            } catch (err) {
                setError(err.message);
                console.error('Error fetching menu:', err);
            } finally {
                setLoading(false);
            }
        };

        if (id) {
            fetchMenuData();
        }
    }, [id]);

    const toggleSection = (idx) => {
        setDisplayWhole((prev) => ({
            ...prev,
            [idx]: !prev[idx],
        }));
    };

    const handleAddToCart = async (item) => {
        try {
            clearError();
            const itemId = item._id || item.id;

            if (!itemId) {
                console.error('Item missing ID field:', item);
                return;
            }

            await addToCart(itemId, 1);
        } catch (error) {
            console.error('Error adding to cart:', error);
        }
    };

    const handleUpdateQuantity = async (item, newQuantity) => {
        try {
            clearError();
            const itemId = item._id || item.id;

            if (newQuantity === 0) {
                await removeFromCart(itemId);
            } else {
                await updateQuantity(itemId, newQuantity);
            }
        } catch (error) {
            console.error('Error updating quantity:', error);
        }
    };

    useEffect(() => {
        if (showCategories) {
            document.body.style.overflow = "hidden";
        } else {
            document.body.style.overflow = "auto";
        }

        return () => {
            document.body.style.overflow = "auto"; // cleanup on unmount
        };
    }, [showCategories]);

    // const handleRemoveFromCart = async (item) => {
    //     try {
    //         clearError();
    //         const itemId = item._id || item.id;
    //         await removeFromCart(itemId);
    //     } catch (error) {
    //         console.error('Error removing from cart:', error);
    //     }
    // };

    // Loading state
    if (loading) {
        return (
            <section className="py-16 px-4 bg-snowbg min-h-screen flex items-center justify-center">
                <div className="text-center">
                    <Loader className="w-8 h-8 animate-spin text-redtxt mx-auto mb-4" />
                    <p className="text-lightGray">Loading menu...</p>
                </div>
            </section>
        );
    }

    // Error state
    if (error) {
        return (
            <section className="py-16 px-4 bg-snowbg min-h-screen flex items-center justify-center">
                <div className="text-center">
                    <p className="text-redtxt text-lg mb-2">Error loading menu</p>
                    <p className="text-lightGray">{error}</p>
                    <button
                        onClick={() => window.location.reload()}
                        className="mt-4 px-4 py-2 bg-redtxt text-white rounded-lg hover:bg-red-600 transition"
                    >
                        Try Again
                    </button>
                </div>
            </section>
        );
    }

    // Main render
    return (
        <section className="py-14 md:py-16 px-4 bg-snowbg">
            <div className="container mx-auto max-w-3xl">
                <motion.div
                    initial={{ y: 30, opacity: 0 }}
                    whileInView={{ y: 0, opacity: 1 }}
                    transition={{ duration: 0.5 }}
                    viewport={{ once: true }}
                    className="mb-12"
                >
                    <h3 className="text-lightGray text-sm md:text-base max-w-full mx-auto text-left pb-2">
                        <Link to='/'>Home</Link> / <Link to="/restaurants">Restaurant</Link> / {restaurant?.name || 'Restaurant'}
                    </h3>
                    <h2 className="text-redtxt text-2xl md:text-4xl font-bold mb-4 text-left">
                        Explore {restaurant?.name || 'Restaurant'}'s Menu
                    </h2>
                </motion.div>

                <div className="flex flex-col gap-8">
                    <motion.div
                        initial={{ y: 30, opacity: 0 }}
                        whileInView={{ y: 0, opacity: 1 }}
                        transition={{ duration: 0.5 }}
                        viewport={{ once: true }}
                        className="relative w-full h-48 md:h-52 rounded-2xl shadow-lg bg-center bg-cover bg-no-repeat"
                        style={{ backgroundImage: restaurant?.image ? `url(${restaurant.image})` : undefined }}>
                        <div className="absolute inset-0 bg-black/60 rounded-2xl"></div>
                        <div className="absolute inset-0 flex items-center justify-between p-6">
                            <div>
                                <h3 className="text-snowtxt text-xl md:text-2xl font-bold mb-1">
                                    {restaurant?.name || 'Restaurant Menu'}
                                </h3>
                                <p className="text-snowtxt text-sm md:text-base font-semibold">
                                    {restaurant?.address || 'Delicious Food & Beverages'}
                                </p>
                            </div>
                            {restaurant?.isOpen !== undefined && (
                                <span className={`px-3 py-1 rounded-full text-sm font-semibold ${restaurant.isOpen
                                    ? 'bg-green-600/80 text-snowtxt'
                                    : 'bg-red-600/80 text-snowtxt'
                                    }`}>
                                    {restaurant.isOpen ? 'Open' : 'Closed'}
                                </span>
                            )}
                        </div>
                    </motion.div>

                    {/* Cart Error Display */}
                    {cartError && (
                        <motion.div
                            initial={{ opacity: 0, y: -20 }}
                            animate={{ opacity: 1, y: 0 }}
                            className="w-full sticky top-20 left-0 z-50 flex items-center justify-between p-4 bg-red-50 border border-red-200 rounded-lg"
                        >
                            <p className="w-[95%] text-red-600 text-sm font-semibold">{cartError}</p>
                            <X
                                onClick={clearError}
                                className="w-5 h-5 cursor-pointer text-red-600 underline mt-1" />
                        </motion.div>
                    )}

                    <motion.div
                        initial={{ y: 30, opacity: 0 }}
                        whileInView={{ y: 0, opacity: 1 }}
                        transition={{ duration: 0.5 }}
                        viewport={{ once: true }}
                        className="w-full bg-white rounded-2xl shadow-lg p-6 space-y-8"
                    >
                        {menuData.length === 0 ? (
                            <div className="text-center py-8">
                                <p className="text-lightGray">No menu items available</p>
                            </div>
                        ) : (
                            menuData.map((section, idx) => {
                                const isOpen = displayWhole[idx] ?? true;
                                return (
                                    <div id={`section-${idx}`} key={`section-${idx}-${section.category}`}>
                                        {/* Section header */}
                                        <div className="border-b border-lightGray pb-3 flex items-center justify-between">
                                            <h3 className="text-redtxt text-xl md:text-2xl font-bold">{section.category}</h3>
                                            <button
                                                type="button"
                                                onClick={() => toggleSection(idx)}
                                                className="cursor-pointer"
                                            >
                                                {isOpen ? (
                                                    <ChevronUp className="w-7 h-7 text-lightGray hover:text-redtxt transition" />
                                                ) : (
                                                    <ChevronDown className="w-7 h-7 text-lightGray hover:text-redtxt transition" />
                                                )}
                                            </button>
                                        </div>

                                        {/* Section items */}
                                        <ul className={isOpen ? "block" : "hidden"}>
                                            {section.items?.map((item, i) => {
                                                const quantity = getItemQuantity(item._id || item.id);
                                                const canAddToCart = item.isAvailable && restaurant?.isOpen;

                                                return (
                                                    <li
                                                        key={item._id ? `item-${item._id}` : `temp-${idx}-${i}`}
                                                        className="flex flex-col sm:flex-row sm:items-center sm:justify-between border-b border-snowbg py-3 gap-3"
                                                    >
                                                        {/* Item left part */}
                                                        <div className="flex items-center sm:items-center gap-4 flex-1">
                                                            <div className="hidden md:block max-sm:w-14 max-sm:h-14 w-20 h-20 border rounded-lg flex-shrink-0 overflow-hidden bg-gray-100">
                                                                {item.image ? (
                                                                    <img
                                                                        src={item.image}
                                                                        alt={item.name}
                                                                        className="w-full h-full object-cover rounded-lg"
                                                                        onError={(e) => {
                                                                            e.currentTarget.style.display = "none";
                                                                        }}
                                                                    />
                                                                ) : (
                                                                    <div className="w-full h-full flex items-center justify-center text-lightGray">
                                                                        <span className="text-xs">No Image</span>
                                                                    </div>
                                                                )}
                                                            </div>
                                                            <div className="flex flex-col flex-1">
                                                                <div className="flex flex-wrap items-center gap-2 mb-1">
                                                                    <span className="text-darktxt text-base md:text-lg font-semibold break-words">
                                                                        {item.name}
                                                                    </span>
                                                                    {!item.isAvailable && (
                                                                        <span className="text-xs bg-red-100 text-red-600 px-2 py-1 rounded-full">
                                                                            Unavailable
                                                                        </span>
                                                                    )}
                                                                    {!restaurant?.isOpen && (
                                                                        <span className="text-xs bg-gray-100 text-gray-600 px-2 py-1 rounded-full">
                                                                            Restaurant Closed
                                                                        </span>
                                                                    )}
                                                                </div>
                                                                <span className="text-lightGray text-base mb-1 line-clamp-2 sm:line-clamp-none">
                                                                    {item.description}
                                                                </span>
                                                                <span className="text-redtxt text-base md:text-lg font-semibold">
                                                                    ₹{item.price}
                                                                </span>
                                                            </div>
                                                        </div>

                                                        {/* Cart Controls */}
                                                        {quantity > 0 ? (
                                                            <motion.div
                                                                initial={{ scale: 0.8, opacity: 0 }}
                                                                animate={{ scale: 1, opacity: 1 }}
                                                                transition={{ duration: 0.2 }}
                                                                className="flex items-center justify-center gap-2 border border-gray-300 px-2 py-1 rounded-md"
                                                            >
                                                                <button
                                                                    onClick={() =>
                                                                        handleUpdateQuantity(item, quantity - 1)
                                                                    }
                                                                    disabled={cartLoading}
                                                                    className="w-7 h-7 flex items-center justify-center disabled:opacity-50"
                                                                >
                                                                    <Minus className="w-4 h-4 text-redtxt" />
                                                                </button>
                                                                <span className="w-8 h-7 flex items-center justify-center font-medium text-redtxt text-sm">
                                                                    {cartLoading ? "..." : quantity}
                                                                </span>
                                                                <button
                                                                    onClick={() =>
                                                                        handleUpdateQuantity(item, quantity + 1)
                                                                    }
                                                                    disabled={cartLoading}
                                                                    className="w-7 h-7 flex items-center justify-center disabled:opacity-50"
                                                                >
                                                                    <Plus className="w-4 h-4 text-redtxt" />
                                                                </button>
                                                            </motion.div>
                                                        ) : canAddToCart ? (
                                                            <motion.button
                                                                whileTap={{ scale: 0.9 }}
                                                                whileHover={{ scale: 1.1 }}
                                                                onClick={() => handleAddToCart(item)}
                                                                disabled={cartLoading}
                                                                className="p-2 max-sm:border max-sm:border-gray-300 rounded-md disabled:opacity-50 w-full sm:w-auto flex items-center justify-center"
                                                            >
                                                                <motion.div
                                                                    initial={{ scale: 1 }}
                                                                    animate={{ scale: [1, 1.3, 1] }}
                                                                    transition={{ duration: 0.4 }}
                                                                    key={quantity}
                                                                >
                                                                    <ShoppingCart className="w-5 h-5 text-lightGray hover:text-redtxt" />
                                                                </motion.div>
                                                            </motion.button>
                                                        ) : (
                                                            <span className="text-sm text-red-500 font-medium w-full sm:w-auto text-center">
                                                                {!item.isAvailable ? "Not Available" : "Restaurant Closed"}
                                                            </span>
                                                        )}
                                                    </li>
                                                );
                                            })}
                                        </ul>
                                    </div>
                                );
                            })
                        )}

                        {cart.itemCount === 0 ? (
                            <div
                                onClick={() => setShowCategories(true)}
                                className="flex items-center justify-end sticky bottom-4 max-sm:bottom-[20px] z-40">
                                <div className="max-sm:w-14 max-sm:h-14 w-16 h-16 bg-black/80 text-snowtxt text-sm md:text-base rounded-full flex items-center justify-center font-semibold shadow-lg cursor-pointer">
                                    Menu
                                </div>
                            </div>
                        ) : (
                            <div className="flex items-center justify-end sticky max-sm:bottom-[70px] bottom-24 z-40">
                                <div
                                    onClick={() => setShowCategories(true)}
                                    className="max-sm:w-14 max-sm:h-14 w-16 h-16 bg-black/80 text-snowtxt text-sm md:text-base  rounded-full flex items-center justify-center font-semibold shadow-lg cursor-pointer">
                                    Menu
                                </div>
                            </div>
                        )}
                    </motion.div>

                    {/* Cart Summary */}
                    {cart.itemCount > 0 && (
                        <motion.div
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            className="sticky bottom-0 left-0 w-full z-30 py-2 md:py-4 px-6 bg-green-50 border-t border-green-200 shadow-lg rounded-lg"
                        >
                            <div className="flex items-center justify-between">
                                <div>
                                    <p className="text-green-700 text-sm md:text-base font-semibold">
                                        {cart.itemCount} item{cart.itemCount > 1 ? 's' : ''} in cart
                                    </p>
                                    <p className="text-green-600 text-sm">
                                        Total: ₹{cart.totalAmount}
                                    </p>
                                </div>
                                <Link
                                    to="/cart"
                                    className="px-3 py-1 bg-green-600 text-white rounded-lg hover:bg-green-700 transition text-base"
                                >
                                    View Cart
                                </Link>
                            </div>
                        </motion.div>
                    )}
                </div>
            </div>

            {/* Categories pop-up */}
            {showCategories && (
                <div
                    className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center"
                    onClick={() => setShowCategories(false)} // click outside closes modal
                >
                    <div
                        className="bg-white rounded-2xl shadow-lg p-6 w-80 max-h-[70vh] overflow-y-auto"
                        onClick={(e) => e.stopPropagation()} // prevent closing on inside clicks
                    >
                        <h3 className="text-lg font-bold mb-4">Categories</h3>
                        <ul className="space-y-3">
                            {menuData.map((section, idx) => (
                                <li key={idx}>
                                    <button
                                        className="w-full text-left px-3 py-2 rounded-lg hover:bg-snowbg text-darktxt font-medium"
                                        onClick={() => {
                                            const el = document.getElementById(`section-${idx}`);
                                            if (el) {
                                                const headerOffset = 80;
                                                const elementPosition = el.getBoundingClientRect().top + window.scrollY;
                                                const offsetPosition = elementPosition - headerOffset;

                                                window.scrollTo({ top: offsetPosition, behavior: "smooth" });
                                            }
                                            setShowCategories(false);
                                        }}
                                    >
                                        {section.category}
                                    </button>
                                </li>
                            ))}
                        </ul>
                    </div>
                </div>
            )}
        </section>
    );
};

export default RestaurantMenu;